/*
 * cart-attributes — pre-checkout delivery details on the cart page.
 *
 * Writes Shopify cart attributes (saved on the order, synced to Make.com/Airtable):
 *   attributes[Delivery Date]   MM/DD/YYYY
 *   attributes[Delivery Time]   11:00am - 5:00pm (single preselected window for now)
 *   attributes[Add Card Message] Yes | No
 *   attributes[Card Message]    free text (only when Add Card Message = Yes)
 *   attributes[Delivery Instructions] free text (optional)
 *
 * Delivery-area eligibility is handled by Shopify's Local Delivery (distance
 * radius) at checkout, so there is no ZIP gate here.
 *
 * Values persist to /cart/update.js on change (mirrors <cart-note>) so they
 * survive AJAX section re-renders and are stored before checkout. The fields
 * also carry form="cart", so they post with the standard Checkout button.
 *
 * Required-field enforcement: the selects are enforced by the browser via the
 * `required` attribute. The date input is NOT, because flatpickr marks it
 * readonly and the HTML spec bars readonly inputs from constraint validation
 * (an order shipped on 2026-08-20 with every attribute except Delivery Date).
 * So the cart form's submit is guarded here (guardSubmit) and any field that is
 * empty blocks checkout with an inline message.
 */
(function () {
  const DEBOUNCE = 500;

  function laNow() {
    // A Date whose local fields reflect Los Angeles wall-clock time.
    return new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
  }

  function midnight(d) {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  }

  // "11:00 AM" / "11am" / "5:30 pm" -> minutes since midnight, or null.
  function parseCutoff(str) {
    if (!str) return null;
    const m = String(str).trim().match(/(\d{1,2})(?::(\d{2}))?\s*([ap]\.?m\.?)?/i);
    if (!m) return null;
    let h = parseInt(m[1], 10);
    const min = m[2] ? parseInt(m[2], 10) : 0;
    const mer = (m[3] || '').toLowerCase();
    if (mer.startsWith('p') && h < 12) h += 12;
    if (mer.startsWith('a') && h === 12) h = 0;
    return h * 60 + min;
  }

  // End time (minutes since midnight) of a delivery window label, or null for "Any".
  function windowEndMinutes(label) {
    const m = String(label).match(/-\s*(\d{1,2})(?::(\d{2}))?\s*([ap]m)/i);
    if (!m) return null;
    let h = parseInt(m[1], 10);
    const min = m[2] ? parseInt(m[2], 10) : 0;
    const mer = (m[3] || '').toLowerCase();
    if (mer.startsWith('p') && h < 12) h += 12;
    if (mer.startsWith('a') && h === 12) h = 0;
    return h * 60 + min;
  }

  if (!customElements.get('cart-attributes')) {
    customElements.define(
      'cart-attributes',
      class CartAttributes extends HTMLElement {
        connectedCallback() {
          if (this._init) return;
          this._init = true;

          this.dateInput = this.querySelector('#delivery-date');
          this.dateNote = this.querySelector('#delivery-date-note');
          this.timeSelect = this.querySelector('#delivery-time');
          this.addCard = this.querySelector('#add-card-message');
          this.cardField = this.querySelector('#card-message-field');
          this.cardMsg = this.querySelector('#card-message');

          this.closedDays = (this.dataset.closedDays || '')
            .split(',')
            .map((n) => parseInt(n.trim(), 10))
            .filter((n) => !Number.isNaN(n));

          this.initDatePicker();
          this.syncCardField();
          this.pruneTimeWindows();
          this.guardSubmit();

          // Card message show/hide + required toggle
          if (this.addCard) {
            this.addCard.addEventListener('change', () => {
              // Clear the message when the customer switches away from "Yes".
              if (this.addCard.value !== 'Yes' && this.cardMsg) this.cardMsg.value = '';
              this.syncCardField();
              this.save();
            });
          }
          if (this.dateInput) {
            this.dateInput.addEventListener('change', () => {
              this.pruneTimeWindows();
              if (this.dateInput.value) this.clearDateError();
            });
          }

          // Debounced persistence for every attribute field
          const debounced = this.debounce(() => this.save(), DEBOUNCE);
          this.querySelectorAll('[name^="attributes["]').forEach((el) => {
            el.addEventListener('change', debounced);
            if (el.tagName === 'TEXTAREA' || el.type === 'text') el.addEventListener('input', debounced);
          });
        }

        debounce(fn, wait) {
          let t;
          return (...args) => {
            clearTimeout(t);
            t = setTimeout(() => fn.apply(this, args), wait);
          };
        }

        initDatePicker() {
          if (!this.dateInput || typeof window.flatpickr !== 'function') return;
          if (this.dateInput._flatpickr) return;

          const la = laNow();
          const cutoff = parseCutoff(this.dataset.cutoff);
          const nowMins = la.getHours() * 60 + la.getMinutes();
          const closed = this.closedDays;
          const blackouts = this.parseBlackouts(); // holiday / closure dates + ranges

          // Earliest selectable day: today unless past cutoff or today is closed.
          const today = midnight(la);
          let min = new Date(today);
          const pastCutoff = cutoff != null && nowMins >= cutoff;
          const todayClosed = closed.includes(min.getDay());
          if (pastCutoff || todayClosed) {
            min = new Date(min.getTime() + 86400000);
          }

          // Walk min forward past closed days / blackouts to the first truly
          // selectable day, for the note under the field.
          const dayBlocked = (d) =>
            closed.includes(d.getDay()) ||
            blackouts.some((b) =>
              b instanceof Date
                ? midnight(b).getTime() === d.getTime()
                : d >= midnight(b.from) && d <= midnight(b.to)
            );
          let earliest = new Date(min);
          for (let i = 0; i < 60 && dayBlocked(earliest); i++) {
            earliest = new Date(earliest.getTime() + 86400000);
          }
          this.renderDateNote({ pastCutoff, todayClosed, today, earliest });

          // Reset a stale saved date: cart attributes persist, so someone who
          // picked "Monday" on Monday and checks out Tuesday would ship a past
          // date. Clearing re-arms the required validation on the field.
          if (this.dateInput.value) {
            const parts = this.dateInput.value.split('/'); // m/d/Y
            const saved = parts.length === 3 ? new Date(+parts[2], +parts[0] - 1, +parts[1]) : null;
            if (!saved || Number.isNaN(saved.getTime()) || saved < min || dayBlocked(midnight(saved))) {
              this.dateInput.value = '';
              this.save(); // scrub the stored attribute too, not just the field
              this._staleCleared = true;
            }
          }

          // Open on the current month even when today is no longer selectable
          // (past cutoff late on the 31st, flatpickr would otherwise open on next
          // month and "31" becomes a month-off trap). Today stays visible, greyed.
          const jumpToToday = (dates, str, inst) => {
            if (!dates.length) inst.jumpToDate(today);
          };

          window.flatpickr(this.dateInput, {
            dateFormat: 'm/d/Y', // submitted value stays MM/DD/YYYY for Make/Airtable
            minDate: min,
            now: today, // "today" ring follows LA time, not the visitor's device clock
            disableMobile: true,
            showMonths: window.innerWidth >= 768 ? 2 : 1, // show next month inline on desktop
            disable: [
              (date) => closed.includes(date.getDay()),
              ...blackouts, // Date objects (single days) + {from,to} objects (ranges)
            ],
            onReady: (dates, str, inst) => {
              jumpToToday(dates, str, inst);
              this.appendCalNote(inst);
            },
            onOpen: jumpToToday,
            onChange: () => {
              this.pruneTimeWindows();
              if (this.dateInput.value) this.clearDateError();
              this.save();
            },
          });

          if (this._staleCleared) {
            this.setDateError('Your saved delivery date is no longer available. Please choose a new date.');
          }
        }

        // Block the cart form's Checkout submit while any delivery detail is
        // empty. The browser already stops empty required selects before the
        // submit event fires; this catches the readonly date input it skips.
        // The guard is attached once per form and looks up the live
        // <cart-attributes> at submit time, so AJAX re-renders of the footer
        // (which replace this element) don't leave a stale reference behind.
        guardSubmit() {
          const form = (this.dateInput && this.dateInput.form) || document.getElementById('cart');
          if (!form || form._cartAttributesGuard) return;
          form._cartAttributesGuard = true;
          form.addEventListener('submit', (event) => {
            const live = document.querySelector('cart-attributes');
            if (live && typeof live.validate === 'function' && !live.validate()) {
              event.preventDefault();
              event.stopImmediatePropagation();
            }
          });
        }

        // True when every required delivery detail has a value. On failure,
        // flags the first empty field, scrolls to it and (for the date) opens
        // the calendar.
        validate() {
          const empty = Array.from(this.querySelectorAll('[required]')).filter(
            (el) => !el.hidden && !el.closest('[hidden]') && !String(el.value || '').trim()
          );
          if (!empty.length) return true;
          const first = empty[0];
          if (first === this.dateInput) {
            this.setDateError('Please choose a delivery date before checking out.');
          } else {
            first.setAttribute('aria-invalid', 'true');
            first.addEventListener('change', () => first.removeAttribute('aria-invalid'), { once: true });
          }
          first.scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (first === this.dateInput && this.dateInput._flatpickr) {
            this.dateInput._flatpickr.open();
          } else {
            first.focus({ preventScroll: true });
          }
          return false;
        }

        setDateError(msg) {
          if (!this.dateInput) return;
          this.dateInput.setAttribute('aria-invalid', 'true');
          if (!this.dateNote) return;
          this.dateNote.textContent = msg;
          this.dateNote.classList.add('cart-attribute__note--error');
          this.dateNote.hidden = false;
        }

        // Drop the error state and restore the regular "earliest date" note
        // (or hide the note when today is selectable).
        clearDateError() {
          if (!this.dateInput) return;
          this.dateInput.removeAttribute('aria-invalid');
          if (!this.dateNote) return;
          this.dateNote.classList.remove('cart-attribute__note--error');
          if (this._noteWhen == null) {
            this.dateNote.hidden = true;
          } else {
            this.fillNote(this.dateNote);
            this.dateNote.hidden = false;
          }
        }

        // Explain why today is missing and name the first available day, e.g.
        // "Today is past our 11:00 AM same-day cutoff. The earliest available
        // date is Saturday, August 1." Shown under the field and repeated as a
        // footer inside the calendar itself. Hidden when today is selectable.
        renderDateNote({ pastCutoff, todayClosed, today, earliest }) {
          this._noteLead = null;
          this._noteWhen = null;
          if (earliest.getTime() > today.getTime()) {
            this._noteWhen = earliest.toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
            });
            const cutoffLabel = (this.dataset.cutoff || '').trim();
            if (pastCutoff && cutoffLabel) {
              this._noteLead = 'Today is past our ' + cutoffLabel + ' same-day cutoff. ';
            } else if (todayClosed) {
              this._noteLead = "We're closed today. ";
            } else {
              this._noteLead = '';
            }
          }
          if (!this.dateNote) return;
          if (this._noteWhen == null) {
            this.dateNote.hidden = true;
            return;
          }
          this.fillNote(this.dateNote);
          this.dateNote.hidden = false;
        }

        fillNote(el) {
          el.textContent = this._noteLead + 'The earliest available date is ';
          const strong = document.createElement('strong');
          strong.textContent = this._noteWhen + '.';
          el.appendChild(strong);
        }

        // Footer bar inside the flatpickr popup, so the "why is today greyed
        // out" answer sits right where the customer is looking.
        appendCalNote(inst) {
          if (this._noteWhen == null || !inst.calendarContainer) return;
          const el = document.createElement('div');
          el.className = 'flatpickr-cal-note';
          this.fillNote(el);
          inst.calendarContainer.appendChild(el);
        }

        // Turn the "Blocked delivery dates" setting (data-blackouts) into flatpickr
        // disable entries. Fail-safe: any malformed line is skipped and logged, never
        // thrown, so a mistyped date can only fail to block a day — it can't break the
        // picker. Accepts "YYYY-MM-DD" (single) and "YYYY-MM-DD to YYYY-MM-DD" (range).
        parseBlackouts() {
          const raw = this.dataset.blackouts || '';
          const out = [];
          raw.split(/[;\n\r,]+/).forEach((line) => {
            const s = line.trim();
            if (!s) return;
            try {
              const range = s.split(/\s+to\s+/i);
              if (range.length === 2) {
                const from = this.parseYMD(range[0]);
                const to = this.parseYMD(range[1]);
                if (from && to && from <= to) {
                  out.push({ from, to });
                } else {
                  console.warn('[cart] ignored blackout range (bad/reversed date):', s);
                }
              } else {
                const d = this.parseYMD(s);
                if (d) out.push(d);
                else console.warn('[cart] ignored blackout date (bad format):', s);
              }
            } catch (e) {
              console.warn('[cart] ignored blackout line:', s);
            }
          });
          if (out.length) console.info('[cart] blackout dates active:', out.length, out);
          return out;
        }

        // Strict "YYYY-MM-DD" -> local-midnight Date, or null. Rejects real-looking but
        // impossible dates (e.g. 2026-02-30) via a round-trip check, and sidesteps the
        // new Date("YYYY-MM-DD") UTC-parsing trap by building the date from its parts.
        parseYMD(str) {
          const m = String(str).trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
          if (!m) return null;
          const y = +m[1];
          const mo = +m[2];
          const da = +m[3];
          const d = new Date(y, mo - 1, da);
          if (d.getFullYear() !== y || d.getMonth() !== mo - 1 || d.getDate() !== da) return null;
          return d;
        }

        syncCardField() {
          if (!this.addCard || !this.cardField) return;
          const wantCard = this.addCard.value === 'Yes';
          this.cardField.hidden = !wantCard;
          if (this.cardMsg) {
            if (wantCard) {
              this.cardMsg.setAttribute('required', '');
            } else {
              this.cardMsg.removeAttribute('required');
            }
          }
        }

        // When the chosen date is today (LA), grey out windows that have already ended.
        pruneTimeWindows() {
          if (!this.timeSelect || !this.dateInput || !this.dateInput.value) return;
          const la = laNow();
          const parts = this.dateInput.value.split('/'); // m/d/Y
          if (parts.length !== 3) return;
          const sel = new Date(+parts[2], +parts[0] - 1, +parts[1]);
          const isToday = midnight(sel).getTime() === midnight(la).getTime();
          const nowMins = la.getHours() * 60 + la.getMinutes();
          Array.from(this.timeSelect.options).forEach((opt) => {
            if (!opt.value) return;
            const end = windowEndMinutes(opt.value);
            const passed = isToday && end != null && nowMins >= end;
            opt.disabled = passed;
            if (passed && opt.selected) {
              this.timeSelect.value = '';
            }
          });
        }

        collect() {
          const attrs = {};
          this.querySelectorAll('[name^="attributes["]').forEach((el) => {
            const m = el.name.match(/^attributes\[(.+)\]$/);
            if (!m) return;
            // Don't persist the card message when the customer said "No".
            if (el.id === 'card-message' && this.addCard && this.addCard.value !== 'Yes') {
              attrs[m[1]] = '';
              return;
            }
            attrs[m[1]] = el.value;
          });
          return attrs;
        }

        save() {
          const body = JSON.stringify({ attributes: this.collect() });
          const cfg = typeof fetchConfig === 'function'
            ? fetchConfig()
            : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
          fetch(`${window.routes.cart_update_url}`, { ...cfg, body }).catch(() => {});
        }
      }
    );
  }
})();
